import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  UserIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import axios from 'axios';

// Public API instance for contact form (no auth required)
const getPublicApi = () => {
  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
  return axios.create({
    baseURL: `${API_URL}/public`,
    headers: { 'Content-Type': 'application/json' }
  });
};

const ContactSales = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  
  // Store the previous location to navigate back
  // Simple path - basename handles /crm prefix automatically
  const previousPath = location.state?.from || '/dashboard';
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    role: '',
    plan: '',
    agencySize: '',
    useCase: '',
    message: ''
  });

  const plans = [
    { value: '', label: 'Select a plan...' },
    { value: 'trial', label: 'Trial (Free)' },
    { value: 'starter', label: 'Starter ($29/month)' },
    { value: 'professional', label: 'Professional ($79/month)' },
    { value: 'enterprise', label: 'Enterprise ($199+/month)' },
    { value: 'custom', label: 'Custom Pricing' }
  ];

  const agencySizes = [
    { value: '', label: 'Select agency size...' },
    { value: 'solo', label: 'Solo Consultant (1-3 users)' },
    { value: 'small', label: 'Small Agency (4-10 users)' },
    { value: 'medium', label: 'Medium Agency (11-25 users)' },
    { value: 'large', label: 'Large Agency (26-50 users)' },
    { value: 'enterprise', label: 'Enterprise (50+ users)' }
  ];

  const useCases = [
    { value: '', label: 'Select use case...' },
    { value: 'relocation', label: 'Relocation Services' },
    { value: 'immigration', label: 'Immigration Services' },
    { value: 'expat', label: 'Expat Services' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'other', label: 'Other' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await getPublicApi().post('/contact', formData);
      const data = response.data;

      if (!data.success) {
        throw new Error(data.message || 'Failed to submit form');
      }

      setSubmitted(true);
      toast.success('Thank you! We\'ll get back to you soon.');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to submit form. Please try again or email us directly at elie@livelovespain.com');
    } finally {
      setLoading(false);
    }
  };

  // Get canonical URL based on hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isSubdomain = hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.');
  const canonicalUrl = isSubdomain 
    ? 'https://crm.livelovespain.com/contact'
    : 'https://www.livelovespain.com/crm/contact';

  // Add JSON-LD schemas
  React.useEffect(() => {
    const baseUrl = isSubdomain ? 'https://crm.livelovespain.com' : 'https://www.livelovespain.com/crm';
    
    // Breadcrumbs Schema
    const breadcrumbsId = 'jsonld-breadcrumbs';
    let breadcrumbsScript = document.getElementById(breadcrumbsId);
    const breadcrumbsPayload = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl + "/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Contact Sales",
          "item": canonicalUrl
        }
      ]
    };
    if (!breadcrumbsScript) {
      breadcrumbsScript = document.createElement('script');
      breadcrumbsScript.type = 'application/ld+json';
      breadcrumbsScript.id = breadcrumbsId;
      document.head.appendChild(breadcrumbsScript);
    }
    breadcrumbsScript.text = JSON.stringify(breadcrumbsPayload);

    // WebPage Schema
    const webpageId = 'jsonld-webpage';
    let webpageScript = document.getElementById(webpageId);
    const webpagePayload = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Contact Sales - LiveLoveSpain CRM",
      "description": "Contact LiveLoveSpain CRM sales team. Get in touch to learn how our CRM can help your relocation agency grow and manage clients more efficiently.",
      "url": canonicalUrl,
      "inLanguage": "en",
      "isPartOf": {
        "@type": "WebSite",
        "name": "LiveLoveSpain CRM",
        "url": baseUrl
      },
      "about": {
        "@type": "Thing",
        "name": "Contact Sales"
      },
      "publisher": {
        "@type": "Organization",
        "name": "LiveLoveSpain"
      },
      "mainEntity": {
        "@type": "ContactPage"
      }
    };
    if (!webpageScript) {
      webpageScript = document.createElement('script');
      webpageScript.type = 'application/ld+json';
      webpageScript.id = webpageId;
      document.head.appendChild(webpageScript);
    }
    webpageScript.text = JSON.stringify(webpagePayload);

    return () => {
      const breadcrumbs = document.getElementById(breadcrumbsId);
      const webpage = document.getElementById(webpageId);
      if (breadcrumbs) breadcrumbs.remove();
      if (webpage) webpage.remove();
    };
  }, [canonicalUrl, isSubdomain]);

  if (submitted) {
    return (
      <>
        <Helmet>
          <title>Contact Sales - LiveLoveSpain CRM</title>
          <meta name="description" content="Contact LiveLoveSpain CRM sales team. Get in touch to learn how our CRM can help your relocation agency grow and manage clients more efficiently." />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content="Contact Sales - LiveLoveSpain CRM" />
          <meta property="og:description" content="Contact LiveLoveSpain CRM sales team. Get in touch to learn how our CRM can help your relocation agency grow and manage clients more efficiently." />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content="Contact Sales - LiveLoveSpain CRM" />
          <meta name="twitter:description" content="Contact LiveLoveSpain CRM sales team. Get in touch to learn how our CRM can help your relocation agency grow and manage clients more efficiently." />
        </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4 py-16">
        <div className="max-w-2xl w-full">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 md:p-12 text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircleIcon className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Thank You!
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              We've received your inquiry and will get back to you within 24 hours.
            </p>
            <div className="space-y-4">
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    name: '',
                    email: '',
                    company: '',
                    phone: '',
                    role: '',
                    plan: '',
                    agencySize: '',
                    useCase: '',
                    message: ''
                  });
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                Submit Another Inquiry
              </button>
              <button
                onClick={() => {
                  // Try to go back, or navigate to dashboard if no history
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate(previousPath);
                  }
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 bg-muted hover:bg-muted/80 text-foreground font-semibold rounded-lg border border-border transition-colors sm:ml-4"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contact Sales - LiveLoveSpain CRM</title>
        <meta name="description" content="Contact LiveLoveSpain CRM sales team. Get in touch to learn how our CRM can help your relocation agency grow and manage clients more efficiently." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Contact Sales - LiveLoveSpain CRM" />
        <meta property="og:description" content="Contact LiveLoveSpain CRM sales team. Get in touch to learn how our CRM can help your relocation agency grow and manage clients more efficiently." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact Sales - LiveLoveSpain CRM" />
        <meta name="twitter:description" content="Contact LiveLoveSpain CRM sales team. Get in touch to learn how our CRM can help your relocation agency grow and manage clients more efficiently." />
      </Helmet>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => {
              // Try to go back, or navigate to dashboard if no history
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate(previousPath);
              }
            }}
            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Contact Sales
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Let's discuss how LiveLoveSpain CRM can help your relocation agency grow. Fill out the form below and we'll get back to you within 24 hours.
            </p>
          </div>

          {/* Contact Form */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 md:p-8 lg:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information Section */}
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2 flex items-center">
                      <PhoneIcon className="h-4 w-4 mr-1" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="+34 612 345 678"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2 flex items-center">
                      <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                      Company/Agency Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Madrid Relocation Agency"
                    />
                  </div>
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-foreground mb-2">
                      Your Role
                    </label>
                    <input
                      type="text"
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Founder, CEO, Manager, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Business Information Section */}
              <div className="border-t border-border pt-6">
                <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Business Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="agencySize" className="block text-sm font-medium text-foreground mb-2">
                      Agency Size
                    </label>
                    <select
                      id="agencySize"
                      name="agencySize"
                      value={formData.agencySize}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      {agencySizes.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="plan" className="block text-sm font-medium text-foreground mb-2">
                      Plan Interest
                    </label>
                    <select
                      id="plan"
                      name="plan"
                      value={formData.plan}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      {plans.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="useCase" className="block text-sm font-medium text-foreground mb-2">
                      Primary Use Case
                    </label>
                    <select
                      id="useCase"
                      name="useCase"
                      value={formData.useCase}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      {useCases.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Message Section */}
              <div className="border-t border-border pt-6">
                <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                  <EnvelopeIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Tell Us More
                </h2>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us about your needs, questions, or how we can help..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  <span className="text-red-500">*</span> Required fields
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <EnvelopeIcon className="h-5 w-5 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Alternative Contact Info */}
          <div className="mt-8 text-center">
            <p className="text-muted-foreground mb-4">
              Prefer to reach us directly?
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a
                href="mailto:elie@livelovespain.com"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                <EnvelopeIcon className="h-5 w-5 mr-2" />
                elie@livelovespain.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ContactSales;

